import React, { useEffect, useRef, useState } from "react";
import { DateTime } from "luxon";
import { apiAttendance } from "../api/attendance";
import { AttendanceDetails, ET, clockLabel, clockTime, duration } from "./AttendanceDetails";

const button = "tw-rounded tw-px-3 tw-py-1 tw-text-xs tw-font-semibold focus:tw-ring-2 focus:tw-ring-blue-500 disabled:tw-opacity-50";
const localFormat = "yyyy-MM-dd'T'HH:mm:ss";
const localValue = (iso) => {
  const d = DateTime.fromISO(iso).setZone(ET);
  return { local: d.toFormat(localFormat), offset: d.offset };
};

// Luxon 1.x silently advances nonexistent times and picks one fall-back offset.
// Round-trip local times, and expose both occurrences of a repeated ET time.
export function clockCandidates(local) {
  if (!local) {return [];}
  const normalized = local.length === 16 ? `${local}:00` : local;
  const guess = DateTime.fromISO(normalized, { zone: ET });
  if (!guess.isValid) {return [];}
  return [guess.minus({ hours: 1 }), guess, guess.plus({ hours: 1 })]
    .filter((d) => d.toFormat(localFormat) === normalized)
    .sort((a, b) => a.toMillis() - b.toMillis());
}

function TimeField({ label, value, onChange }) {
  const candidates = clockCandidates(value.local);
  return <label className="tw-block tw-text-xs tw-mb-2">{label} · ET
    <input type="datetime-local" step="1" required value={value.local} className="tw-block tw-w-full tw-border tw-rounded tw-p-2 tw-mt-1"
      onChange={(e) => {
        const options = clockCandidates(e.target.value);
        onChange({ local: e.target.value, offset: options.some((d) => d.offset === value.offset) ? value.offset : options.length === 1 ? options[0].offset : null });
      }} />
    {value.local && !candidates.length && <span className="tw-text-red-700">This ET time does not exist. Check the daylight-saving change.</span>}
    {candidates.length > 1 && <select required aria-label={`${label} daylight-saving occurrence`} className="tw-block tw-w-full tw-border tw-p-1 tw-mt-1"
      value={value.offset == null ? "" : value.offset} onChange={(e) => onChange({ ...value, offset: e.target.value === "" ? null : Number(e.target.value) })}>
      <option value="">Choose which occurrence</option>
      {candidates.map((d, i) => <option key={d.offset} value={d.offset}>{i === 0 ? "First" : "Second"} occurrence · {d.offsetNameShort}</option>)}
    </select>}
  </label>;
}

const isoValue = (value) => {
  const d = clockCandidates(value.local).find((candidate) => candidate.offset === value.offset);
  if (!d) {throw new Error("Choose a valid ET time and, when repeated, its daylight-saving occurrence.");}
  return d.toUTC().toISO();
};

function CorrectionForm({ session, busy, onSave, onCancel }) {
  const initial = (time) => localValue(time);
  const [start, setStart] = useState(session ? initial(session.startedAt) : { local: "", offset: null });
  const [end, setEnd] = useState(session ? initial(session.endedAt) : { local: "", offset: null });
  const [breaks, setBreaks] = useState((session?.breaks || []).map((b) => ({ start: initial(b.startedAt), end: initial(b.endedAt) })));
  const [reason, setReason] = useState("");
  const [error, setError] = useState("");
  const save = (event) => {
    event.preventDefault();
    try {
      const autoOffset = (v) => v.offset == null && clockCandidates(v.local).length === 1 ? { ...v, offset: clockCandidates(v.local)[0].offset } : v;
      onSave({ action: session ? "correct" : "add", sessionId: session?.id || 0, version: session?.version || 0,
        startedAt: isoValue(autoOffset(start)), endedAt: isoValue(autoOffset(end)), reason: reason.trim(),
        breaks: breaks.map((b) => ({ startedAt: isoValue(autoOffset(b.start)), endedAt: isoValue(autoOffset(b.end)) })) });
    } catch (e) {setError(e.message);}
  };
  return <form onSubmit={save} className="tw-border-t tw-pt-3 tw-mt-3" aria-label={session ? "Correct clock record" : "Add missed clock record"}>
    <h3 className="tw-text-sm tw-font-semibold tw-mb-2">{session ? "Correct time" : "Add missed session"}</h3>
    <p className="tw-text-xs tw-text-gray-600">Your reason and all changes will be visible to managers. Original records are retained.</p>
    <TimeField label="Clock in" value={start} onChange={setStart} />
    <TimeField label="Clock out" value={end} onChange={setEnd} />
    {breaks.map((b, i) => <fieldset key={i} className="tw-border tw-rounded tw-p-2 tw-mb-2"><legend className="tw-text-xs">Break {i + 1}</legend>
      <TimeField label={`Break ${i + 1} start`} value={b.start} onChange={(v) => setBreaks((bs) => bs.map((row, n) => n === i ? { ...row, start: v } : row))} />
      <TimeField label={`Break ${i + 1} end`} value={b.end} onChange={(v) => setBreaks((bs) => bs.map((row, n) => n === i ? { ...row, end: v } : row))} />
      <button type="button" className="tw-text-xs tw-text-red-700 tw-underline" onClick={() => setBreaks((bs) => bs.filter((_, n) => n !== i))}>Remove break</button>
    </fieldset>)}
    <button type="button" className="tw-text-xs tw-text-blue-800 tw-underline tw-mb-2" onClick={() => setBreaks((bs) => [...bs, { start: { ...start }, end: { ...end } }])}>Add break</button>
    <label className="tw-block tw-text-xs">Reason for correction
      <textarea required maxLength={1000} value={reason} onChange={(e) => setReason(e.target.value)} className="tw-block tw-w-full tw-border tw-rounded tw-p-2 tw-mt-1" />
    </label>
    {error && <p role="alert" className="tw-text-xs tw-text-red-700">{error}</p>}
    <div className="tw-flex tw-gap-2 tw-mt-3"><button disabled={busy || !reason.trim()} className={`${button} tw-bg-blue-900 tw-text-white`}>Save correction</button>
      <button type="button" disabled={busy} onClick={onCancel} className={`${button} tw-bg-gray-100`}>Cancel</button></div>
  </form>;
}

function requestId() {
  return Array.from(crypto.getRandomValues(new Uint8Array(16)), (n) => n.toString(16).padStart(2, "0")).join("");
}
// Some browsers pause performance.now() during sleep; neither clock may make old input look fresh.
const inputAge = (input) => input == null ? null : Math.floor(Math.max(
  performance.now() - input.monotonic, Date.now() - input.wallClock,
) / 1000);
const message = (e) => e.response?.data?.errors?.[0]?.message || e.response?.data?.message || e.message || "Clock request failed.";

export default function PersonalAttendanceClock() {
  const [state, setState] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const [day, setDay] = useState("");
  const [editing, setEditing] = useState(null);
  const root = useRef(null);
  const statusButton = useRef(null);
  const pending = useRef(false);
  const alive = useRef(false);
  const current = useRef(null);
  const selectedDay = useRef(day);
  const lastInput = useRef(null);
  const panelOpen = useRef(open);
  panelOpen.current = open;
  const retry = useRef(null);
  selectedDay.current = day;

  const refresh = async (heartbeat = false) => {
    if (pending.current) {return;}
    pending.current = true;
    if (alive.current) {setBusy(true);}
    const selection = selectedDay.current;
    const date = panelOpen.current ? selection : ""; // Server decides today's ET date.
    try {
      if (heartbeat && current.current?.current) {
        const age = inputAge(lastInput.current);
        await apiAttendance.heartbeat(age != null && age <= 120 ? age : null);
      }
      const result = await apiAttendance.get(date);
      if (alive.current && (selection === selectedDay.current || !panelOpen.current)) {
        if (!date) {selectedDay.current = result.day; setDay(result.day);}
        current.current = result; setState(result); setError("");
      }
    } catch (e) {
      if (alive.current) {setState(null); setError(`Clock status unavailable. ${message(e)}`);}
    } finally {
      pending.current = false;
      if (alive.current) {
        setBusy(false);
        if (panelOpen.current && selection !== selectedDay.current) {refresh();}
      }
    }
  };

  useEffect(() => {
    alive.current = true;
    const input = (event) => {
      if (event.isTrusted && !document.hidden && document.hasFocus()) {
        lastInput.current = { monotonic: performance.now(), wallClock: Date.now() };
      }
    };
    const flush = () => {
      if (!current.current?.current || lastInput.current == null) {return;}
      const age = inputAge(lastInput.current);
      if (age <= 120) {apiAttendance.flushActivity(age);}
    };
    const update = () => { if (!document.hidden) {refresh(true);} else {flush();} };
    const outside = (event) => { if (!root.current?.contains(event.target)) {setOpen(false);} };
    const key = (event) => { if (event.key === "Escape") {setOpen(false); statusButton.current?.focus();} };
    const events = ["pointerdown", "mousemove", "keydown", "input", "wheel", "touchstart"];
    events.forEach((name) => document.addEventListener(name, input, { passive: true }));
    document.addEventListener("visibilitychange", update);
    window.addEventListener("pagehide", flush);
    document.addEventListener("mousedown", outside);
    document.addEventListener("keydown", key);
    const timer = window.setInterval(update, 30000);
    refresh();
    return () => {
      alive.current = false;
      window.clearInterval(timer);
      events.forEach((name) => document.removeEventListener(name, input));
      document.removeEventListener("visibilitychange", update);
      window.removeEventListener("pagehide", flush);
      document.removeEventListener("mousedown", outside);
      document.removeEventListener("keydown", key);
    };
  }, []);

  useEffect(() => { if (alive.current) {refresh();} }, [day]);

  const change = async (payload) => {
    if (pending.current) {return;}
    pending.current = true; setBusy(true); setError("");
    const key = JSON.stringify(payload);
    try {
      if (!retry.current || retry.current.key !== key) {retry.current = { key, id: requestId() };}
      await apiAttendance.change({ ...payload, requestId: retry.current.id });
      retry.current = null;
      if (alive.current) {setEditing(null);}
      const result = await apiAttendance.get(panelOpen.current ? selectedDay.current : "");
      if (alive.current) {
        if (!panelOpen.current) {selectedDay.current = result.day; setDay(result.day);}
        current.current = result; setState(result);
      }
    } catch (e) {
      // A lost response may follow a successful write; read server truth before retrying.
      try {
        const result = await apiAttendance.get(panelOpen.current ? selectedDay.current : "");
        if (alive.current) {
          if (!panelOpen.current) {selectedDay.current = result.day; setDay(result.day);}
          current.current = result; setState(result);
          if (payload.action === "clock_in" && result.current || payload.sessionId &&
            (result.current?.id !== payload.sessionId || result.current?.version !== payload.version)) {retry.current = null;}
        }
      } catch (_) { if (alive.current) {setState(null);} }
      if (alive.current) {
        if (e.status === 409) {setEditing(null);}
        setError(`Clock update could not be confirmed. ${message(e)}`);
      }
    } finally {pending.current = false; if (alive.current) {setBusy(false);} }
  };
  const act = (action) => change({ action, sessionId: state?.current?.id || 0, version: state?.current?.version || 0 });
  const clocked = state?.status === "clocked_in";
  const onBreak = state?.status === "on_break";
  const automatic = state?.status === "clocked_out" && ["inactivity", "connection_lost"].includes(state.latest?.endReason);
  const openBreak = state?.current?.breaks?.find((b) => !b.endedAt);
  const breakSeconds = openBreak ? (Date.parse(state.now) - Date.parse(openBreak.startedAt)) / 1000 : 0;

  return <div ref={root} className="tw-relative tw-text-gray-800" aria-label="Personal attendance clock">
    <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-end tw-gap-2 tw-py-2">
      <button ref={statusButton} type="button" className={`${button} tw-flex tw-items-center tw-gap-2`} aria-expanded={open} aria-controls="my-clock-panel" onClick={() => setOpen(!open)}>
        <span aria-hidden="true" className={`tw-w-2 tw-h-2 tw-rounded-full ${clocked ? "tw-bg-green-500" : onBreak ? "tw-bg-yellow-500" : "tw-bg-gray-400"}`} />
        {state ? clockLabel(state.status) : error ? "Clock unavailable" : "Loading clock…"}
        {clocked && <span className="tw-font-normal">· Started {clockTime(state.current.startedAt)}</span>}
        {onBreak && <span className="tw-font-normal">· {duration(breakSeconds)}</span>}
      </button>
      {state && <>
        {!state.current && <button type="button" disabled={busy} onClick={() => act("clock_in")}
          className={`${button} tw-bg-blue-900 tw-text-white`}>{automatic ? "Clock back in" : "Clock in"}</button>}
        {clocked && <button type="button" disabled={busy} onClick={() => act("start_break")} className={`${button} tw-bg-gray-100`}>Start break</button>}
        {onBreak && <button type="button" disabled={busy} onClick={() => act("end_break")} className={`${button} tw-bg-blue-900 tw-text-white`}>End break</button>}
        {state.current && <button type="button" disabled={busy} onClick={() => act("clock_out")} className={`${button} tw-border tw-border-gray-300`}>Clock out</button>}
      </>}
      {error && <button type="button" disabled={busy} onClick={() => refresh()} className={`${button} tw-text-blue-800 tw-underline`}>Retry</button>}
    </div>
    {automatic && <div className="tw-text-xs tw-text-yellow-900 tw-bg-yellow-50 tw-px-3 tw-py-2">
      Automatically clocked out at {clockTime(state.latest.recordedEndedAt)} · {state.latest.endReason === "connection_lost" ? "connection lost" : "no activity"} for 15 minutes.
      {state.latest.corrected && <span> Time corrected.</span>}
      <button type="button" className="tw-ml-2 tw-underline" onClick={() => {
        setOpen(true); setDay(DateTime.fromISO(state.latest.startedAt).setZone(ET).toISODate()); setEditing(state.latest);
      }}>Correct time</button>
    </div>}
    {error && <p role="alert" className="tw-text-xs tw-text-red-700 tw-m-0 tw-py-1">{error}</p>}
    {open && <div id="my-clock-panel" role="region" aria-label="My clock records"
      className="tw-absolute tw-right-0 tw-top-full tw-z-50 tw-bg-white tw-border tw-border-gray-200 tw-rounded-lg tw-shadow-lg tw-p-4 tw-overflow-y-auto"
      style={{ width: "min(460px, calc(100vw - 24px))", maxHeight: "75vh" }}>
      <div className="tw-flex tw-justify-between tw-items-center tw-mb-3"><strong>My clock</strong>
        <button type="button" aria-label="Close clock records" onClick={() => setOpen(false)}>✕</button></div>
      <label className="tw-block tw-text-xs tw-mb-3">Records date · ET
        <input type="date" value={day} onChange={(e) => {if (e.target.value) {setEditing(null); setDay(e.target.value);}}} className="tw-block tw-border tw-rounded tw-p-1 tw-mt-1" />
      </label>
      {state && state.day === day
        ? <AttendanceDetails state={state} onCorrect={(s) => setEditing(s)} onAdd={() => setEditing({ adding: true })} />
        : <p className="tw-text-sm">{error ? "Records unavailable." : "Loading records…"}</p>}
      {editing && <CorrectionForm key={`${editing.id || "new"}-${day}`} session={editing.adding ? null : editing}
        busy={busy} onSave={change} onCancel={() => setEditing(null)} />}
      <p className="tw-text-xs tw-text-gray-500 tw-mt-3 tw-mb-0">
        After 15 minutes without qualifying activity, the clock ends at the last confirmed activity.
        Explicit breaks are exempt. Closing this panel does not clock you out.
      </p>
    </div>}
  </div>;
}
